import { useContext, useRef, useState } from 'react';
import AppContext from '../contexts/AppContext';
import DataContext from '../contexts/DataContext';
import { useNavigate } from 'react-router-dom';

import { appendScript } from '../helpers';

import TextInput from './Input';
import Button from './Button';
import PhoneNumberInput from './PhoneNumberInput';
import { useSearchParams } from 'react-router-dom';

export default function PhoneValidation({
  slideKey,
  updateOtpMode,
  navigateProcessing,
}: any) {
  const { urlParams, slfApiUrl, isShortISRFeatureFormCustomer } =
    useContext(AppContext);
  const { data, updateData } = useContext(DataContext);
  const navigate = useNavigate();

  // State for errors and entry
  const [originalPhone, setOriginalPhone] = useState('');
  const [otpHaveError, setOtpHaveError] = useState(false);
  const [entryMode, setEntryMode] = useState(false);
  const [codeInputValue, setCodeInputValue] = useState('');
  const [phoneInputValue, setPhoneInputValue] = useState('');

  const [warningOpen, setWarningOpen] = useState(false);
  const [warning, setWarning] = useState('');

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertData, setAlertData] = useState({
    title: 'Oops, error with phone number.',
    first: 'Phone number not provisioned with any carrier.',
    second: 'Please enter a valid phone number.',
  });

  const [searchParams] = useSearchParams();
  const currentParams = Object.fromEntries(searchParams.entries());

  const sentCode = useRef(false);

  const setEntryModeManage = (mode: boolean) => {
    if (entryMode) {
      setPhoneInputValue(originalPhone);
    }
    setEntryMode(mode);
    updateOtpMode(mode);
  };

  // Format number to (000) 000-0000 for input box
  const formatPhoneNumber = (phone: string) => {
    var cleaned = ('' + phone).replace('+1', '').replace(/\D/g, '');
    var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return '';
  };

  if (slideKey === 'otp_verify' && !sentCode.current) {
    sentCode.current = true;
    appendScript(
      `${slfApiUrl}/api/user/update_lead_meta/` +
        data.leadId +
        '/otp_verification/0',
      true
    );
    if (data.phone !== undefined) {
      const formattedNumber = formatPhoneNumber(data.phone);
      setOriginalPhone(formattedNumber);
      setPhoneInputValue(formattedNumber);
      appendScript(
        `https://geoip.solarleadfactory.ai/phone-check/?via=sms&phone_number=${data.phone}&action=start`,
        true
      );
    } else {
      setEntryMode(true);
    }
  }

  // Resend code from Twilio API
  const resendCode = () => {
    appendScript(
      `https://geoip.solarleadfactory.ai/phone-check/?via=sms&phone_number=${originalPhone.replace(
        /\D/g,
        ''
      )}&action=start`,
      true
    );
  };

  // Note: Currently unused in production
  window.routeUser = () => {};

  // Callback after updating phone number
  window.phoneUpdated = (phoneData: any) => {
    appendScript(
      `${slfApiUrl}/api/user/get_lead_details/` +
        data.leadId +
        `?callback=routeUser`,
      true
    );
  };

  // Submit number to SMS service to get code
  const requestVerificationCode = () => {
    setWarningOpen(false);
    var phonepattern = /\(\d{3}\)[ ]?\d{3}[-]?\d{4}$/;
    if (!phoneInputValue.match(phonepattern) || !phoneInputValue.length) {
      setAlertData({
        title: 'Oops, error with phone number.',
        first: 'Phone number not provisioned with any carrier.',
        second: 'Please enter a valid phone number.',
      });
      setAlertOpen(true);
      // TO-DO: Focus on the phone input
      return false;
    } else {
      setOriginalPhone(phoneInputValue.replace(/\D/g, ''));
      appendScript(
        `${slfApiUrl}/api/user/update_phone/` +
          data.leadId +
          `/${encodeURIComponent(
            phoneInputValue.replace(/\D/g, '')
          )}?callback=phoneUpdated`,
        true
      );
      appendScript(
        `https://geoip.solarleadfactory.ai/phone-check/?via=sms&phone_number=${phoneInputValue.replace(
          /\D/g,
          ''
        )}&action=start`,
        true
      );
      setEntryMode(false);
    }
  };

  // Submit to SMS service to verify code
  const verifyVerificationCode = (phoneData: any) => {
    if (
      (!!currentParams.bypassOTP && currentParams.bypassOTP === 'true') ||
      codeInputValue === '01010101'
    ) {
      if (isShortISRFeatureFormCustomer) {
        navigate(`/${urlParams}`);
      } else {
        navigateProcessing();
      }
    } else {
      setWarningOpen(false);
      if (codeInputValue.length) {
        const endpoint = `https://geoip.solarleadfactory.ai/phone-check/?verification_code=${codeInputValue}&phone_number=${originalPhone.replace(
          /\D/g,
          ''
        )}&action=check`;
        appendScript(endpoint, true);
      } else {
        setWarning('Please enter a valid code');
        setWarningOpen(true);
      }
    }
  };

  // Invalid phone message responses from API
  const invalidPhone = [
    'Phone number not provisioned with any carrier. Please enter a valid phone number.',
    'Cannot send SMS to landline phone numbers',
    'Phone number is invalid',
  ];

  // Verify SMS success
  window.verifyPhoneCallback = (phoneData: any) => {
    if (
      typeof phoneData === 'object' &&
      !Array.isArray(phoneData) &&
      phoneData !== null
    ) {
      if (!phoneData.success) {
        if (invalidPhone.includes(phoneData.message)) {
          setAlertData({
            title: 'Oops! Your phone number seems to be incorrect.',
            first: 'Please enter a valid phone number.',
            second: 'If you feel this is an error please re-enter your number.',
          });
          setAlertOpen(true);
          setEntryMode(true);
        } else {
          setAlertData({
            title: 'Oops, error with your code.',
            first: 'The code you entered is invalid.',
            second: 'Please check it and try again or enter a new number.',
          });
          setAlertOpen(true);
        }
      } else if (phoneData.action === 'check' && phoneData.success) {
        dataLayer.push({
          event: 'seform_phone_number_verified',
        });

        appendScript(
          `${slfApiUrl}/api/user/update_lead_meta/` +
            data.leadId +
            '/sandbox_end/1',
          true
        );
        appendScript(
          `${slfApiUrl}/api/user/update_lead_meta/` +
            data.leadId +
            '/otp_verification/1',
          true
        );
        if (isShortISRFeatureFormCustomer) {
          navigate(`/${urlParams}`);
        } else {
          navigateProcessing();
        }
      }
    }
  };

  return (
    <div className="relative">
      <p className="mt-[15px] md:mt-10 text-[17px] md:text-[18px]">
        {entryMode
          ? 'We will send you the 4 digit verification code.'
          : 'Enter the code that has been sent to your phone.'}
      </p>
      {entryMode ? (
        <PhoneNumberInput
          label=""
          value={phoneInputValue}
          onChange={(newValue: any) => {
            setPhoneInputValue(newValue);
            updateData({
              ...data,
              phone: newValue,
            });
          }}
        />
      ) : (
        <TextInput
          name="otp"
          type="number"
          placeholder="Verification code"
          className="mt-3 md:mt-4 md:w-[400px]"
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            const eventValue: string = e.currentTarget.value;
            const haveError = !eventValue || eventValue.trim().length < 1;
            setOtpHaveError(haveError);
            setCodeInputValue(eventValue);
          }}
          haveError={otpHaveError}
          value={codeInputValue}
        />
      )}

      <div
        className={`warning ${
          warningOpen ? '' : 'in'
        }visbile mt-2 text-red-600 text-sm${entryMode ? ' -mt-30' : ''}`}>
        {warning}
      </div>

      <div className="mt-5 md:mt-10">
        <Button
          title={entryMode ? 'Send verification code' : 'Verify'}
          action={entryMode ? requestVerificationCode : verifyVerificationCode}
          disabled={
            entryMode ? phoneInputValue.length < 14 : !codeInputValue.length
          }
          data-zuko-id={`${entryMode ? 'verify' : 'send-verification-code'}`}
        />
      </div>
      <div className="mt-5 md:mt-8 w-full short:!w-[350px] md-max:max-w-[279px] md:w-[400px] flex flex-row justify-center items-center">
        {!entryMode && (
          <>
            <button
              className="text-black text-[15px] md:text-[18px] font-medium underline"
              type="button"
              onClick={() => resendCode()}
              data-zuko-id="resend-code">
              Resend code
            </button>
            &nbsp;/&nbsp;
          </>
        )}
        <button
          className="text-black text-[15px] md:text-[18px] font-medium underline"
          type="button"
          onClick={() => setEntryModeManage(!entryMode)}
          data-zuko-id={`${
            entryMode ? 'enter-a-different-number' : 'go-back'
          }`}>
          {entryMode ? 'Go back' : 'Enter a different number'}
        </button>
      </div>

      {alertOpen && (
        <div className="alertModal">
          <div className="alert w-[100%] md:w-[663px] p-6 md:py-[60px] md:px-[40px]">
            <div className="alertTitle text-2xl md:text-[36px]">
              {alertData.title}
            </div>
            <div className="alertText text-[17px] md:text-[20px]">
              {alertData.first}
              <br />
              {alertData.second}
            </div>
            <button
              className="alertButton w-[100%] md:w-[400px]"
              onClick={() => setAlertOpen(false)}
              data-zuko-id="error-with-phone-number-okay">
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
